import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import "./TeaserForm.scss";

import Checkbox from "../Checkox/Checkbox";
import Select from "../Select/Select";
import InputField from "../InputField/InputField";
import CpcInputField from "../InputField/CpcInputField";
import FormMessage from "../FormMessage/FormMessage";
import TtlSelector from "./TtlSelector";
import Button from "../../ui/Button/Button";

import ImageFieldComponent from "./ImageField";
import ImageView from "./ImageView";
import RejectReasons from "./RejectReasons";
import ImageGrid from "./ImageGrid";

import CategoriesSelect from "../../components/CategoriesSelect";

import { showTypeOptions, teaserStatus, campaignTypes } from "../../constants";

import moment from "moment";
import IfPermission from "../../components/IfPermission";
import * as perm from "../../utils/perm";

import classnames from "classnames";
import useGlobals from "../../hooks/useGlobals";
import { TreeSelect } from "../TreeSelect";
import { Field } from "../Field";

const TeaserForm = ({
  fields,
  onChange,
  errors,
  campaignInfo,
  spellChecker,
  aiTitlesLoading,
  handleTitleGenerate,
  aiTitles = { titles: [], keywords: [] },
  allowAiTitles
}) => {
  const campaignCpc = campaignInfo.cpc;
  const tagScheme = campaignInfo.tag_scheme;
  const globals = useGlobals();

  const taxonomyItems = useMemo(() => {
    if (!globals.taxonomy) {
      return [];
    }

    const result = [];

    const loop = (taxonomies, parent) => {
      taxonomies.forEach(taxonomy => {
        result.push({
          label: taxonomy.name,
          value: taxonomy.id,
          isVisible: taxonomy.is_visible,
          parent
        });

        if (taxonomy.children) {
          loop(taxonomy.children, taxonomy.id);
        }
      });
    };

    loop(globals.taxonomy);

    return result;
  }, [globals.taxonomy]);

  const mpaaRatingOptions =
    globals?.mpaa_rating?.map(rating => {
      return {
        label: rating.name,
        value: rating.id
      };
    }) ?? [];

  const handleChange = (e, props) => {
    const name = props?.name || e.target.name;
    const value = props?.value || e.target.value;
    onChange({ [name]: value });
  };

  const handleTtlChange = date => {
    if (date) {
      onChange({ active_until: moment(date).format("YYYY-MM-DD") });
    } else {
      onChange({ active_until: null });
    }
  };

  const handleCpcChange = e => {
    onChange({ cpc: e.target.value, use_campaign_cpc: false });
  };

  const handleUseCampaignCpc = e => {
    onChange({ use_campaign_cpc: e.target.checked });
  };

  const handleTypeChange = type => {
    onChange({ type: type.value });
  };

  const handleCategoryChange = category => {
    onChange({ category: category.value });
  };

  const handleImageChange = name => {
    onChange({ image: name });
  };

  const handleTaxonomyChange = taxonomy => {
    onChange({ taxonomy });
  };

  const handleMpaaRatingChange = ({ value: mpaa_rating }) => {
    onChange({ mpaa_rating });
  };

  const handleAiTitleChange = ({ value: title }) => {
    onChange({ title });
  };

  const handleInreadChange = event => {
    onChange({ inread: event.target.checked });
  };

  function isCatActive() {
    if (
      campaignInfo.category_group &&
      campaignInfo.category_group.length != 0
    ) {
      return true;
    }

    if (
      fields.campaign_category_group &&
      fields.campaign_category_group.length != 0
    ) {
      return true;
    }

    return false;
  }

  const isInReadFieldVisible = fields.inread ?? false;

  const aiTitleOptions = aiTitles.titles.map(title => ({
    label: title,
    value: title
  }));

  return (
    <div className="teaser-form">
      {(fields.published == teaserStatus.reject ||
        fields.published == teaserStatus.moderate) && (
        <RejectReasons reasons={fields.reject_reasons} />
      )}
      <div className="teaser-form__form">
        <div className="teaser-form__fields">
          <div className="teaser-form__selects">
            <CategoriesSelect
              className="category"
              placeholder="Выбор категории"
              onSelect={handleCategoryChange}
              value={fields.category}
              group={
                campaignInfo.category_group || fields.campaign_category_group
              }
              //disabled={!(campaignInfo.category_group || fields.campaign_category_group)}
              disabled={!isCatActive()}
              key={`${fields.category}-category`}
            />
            <IfPermission checker={perm.canChangeArea}>
              <Select
                className="all"
                options={showTypeOptions}
                onSelect={handleTypeChange}
                value={fields.type}
                key={`${fields.type}-type`}
              />
            </IfPermission>
            <TtlSelector
              onChange={handleTtlChange}
              date={fields.active_until}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
              gap: 25,
              marginBottom: 25
            }}
          >
            <div className={"teaser-form__taxonomy"}>
              <Field label={"Таксономия"}>
                <TreeSelect
                  value={fields.taxonomy ?? []}
                  items={taxonomyItems}
                  onChange={handleTaxonomyChange}
                />
              </Field>
            </div>
            <Select
              value={fields.mpaa_rating}
              options={mpaaRatingOptions}
              placeholder={"Выбор рейтинга MPA"}
              label={"Рейтинг MPA"}
              className={"teaser-form__rating"}
              onSelect={handleMpaaRatingChange}
            />
          </div>

          {allowAiTitles && (
            <IfPermission checker={perm.canGenerateAiTitle}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 3fr)",
                  gap: 25,
                  marginBottom: 25
                }}
              >
                <Button
                  color="red"
                  onClick={handleTitleGenerate}
                  disabled={aiTitlesLoading}
                >
                  Generate Titles
                </Button>

                <Select
                  className="all"
                  loading={aiTitlesLoading}
                  options={aiTitleOptions}
                  onSelect={handleAiTitleChange}
                  value={fields.type}
                  placeholder={"AI-Title"}
                  key="ai-title-select"
                />
              </div>
            </IfPermission>
          )}

          <InputField
            label="Креативный заголовок"
            limit={campaignInfo.is_goods ? 100 : 65}
            onChange={handleChange}
            value={fields.title}
            name="title"
            errors={errors.title}
            spellChecker={spellChecker?.getFieldProps}
          />

          <InputField
            label="Ссылка на материал"
            notes={tagScheme}
            showNoteLabel="Показать метку"
            onChange={handleChange}
            name="url"
            value={fields.url}
            key={campaignInfo.cid}
            errors={errors.url}
          />
          <div
            style={{
              marginBottom: 24
            }}
          >
            <Button
              disabled={fields.url?.includes(
                "https://adsafee.com/af/collector/api/v0/t?"
              )}
              onClick={() => {
                let url = "https://adsafee.com/af/collector/api/v0/t?";
                url +=
                  "url=" + fields.url.replace(/[^%]+/g, encodeURIComponent);
                url += "&project_id=e260dadd-f3cf-6956-a87f-b55188d6cb8a";

                onChange({ url: url.toString() });
              }}
            >
              AF
            </Button>
          </div>

          <InputField
            label="Дисклеймер (необязательно)"
            onChange={handleChange}
            name="disclaimer"
            limit={100}
            value={fields.disclaimer}
            errors={errors.disclaimer}
          />
          <div
            style={{
              marginBottom: 24
            }}
          >
            <Checkbox
              id={"inread"}
              checked={fields.inread ?? false}
              onChange={handleInreadChange}
            >
              Внести данные для показа в In-read форматах
            </Checkbox>
          </div>
          {isInReadFieldVisible && (
            <>
              <InputField
                label="Подводка"
                type="text"
                limit={100}
                onChange={handleChange}
                name="desc"
                value={fields.desc}
                errors={errors.desc}
                spellChecker={spellChecker?.getFieldProps}
              />
              <InputField
                label="Call to Action"
                onChange={handleChange}
                name="cta"
                limit={24}
                value={fields.cta}
                errors={errors.cta}
              />
            </>
          )}
          <ImageFieldComponent
            key={fields.image_url}
            onChange={handleImageChange}
            cropperUrl={fields.image_url}
          />

          {allowAiTitles && (
            <IfPermission checker={perm.canGenerateAiTitle}>
              <ImageGrid
                onImageClick={url => {
                  onChange({ image_url: url });
                }}
                keywords={aiTitles.keywords}
              />
            </IfPermission>
          )}

          {campaignInfo.type && campaignInfo.type != campaignTypes.ex && (
            <div>
              <div className="teaser-form__cpc-group">
                <span className="teaser-form__cpc-label">CPC Анонса</span>
                <IfPermission checker={perm.canChangeCpc}>
                  <CpcInputField
                    placeholder="Ввести CPC..."
                    value={fields.use_campaign_cpc ? campaignCpc : fields.cpc}
                    onChange={handleCpcChange}
                  />
                </IfPermission>
                <Checkbox
                  checked={fields.use_campaign_cpc}
                  onChange={handleUseCampaignCpc}
                  id="use_campaign_cpc"
                >
                  Использовать CPC кампании
                </Checkbox>
              </div>
              <FormMessage messages={errors.cpc} color="error" />
            </div>
          )}
        </div>

        <div
          className={classnames("teaser-form__image", {
            "teaser-form__no-border": fields.image
          })}
        >
          <div className="teaser-form__img-cut-top" />
          <div className="teaser-form__img-cut-bottom" />
          <ImageView name={fields.image} />
        </div>
      </div>
    </div>
  );
};

TeaserForm.propTypes = {
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
};

TeaserForm.defaultProps = {
  fields: {},
  errors: {},
  campaignInfo: {}
};

export default TeaserForm;
